import KitFadeView from '@omni/kit/components/KitFadeView';
import KitText from '@omni/kit/components/KitText';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import formatTooltipDuration from '@omni/kit/utilities/formatTooltipDuration';
import { transparentHexColor } from '@omni/kit/utilities/utilities';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { usePlayerVideoCurrentTime } from '../../../../../PlayerVideo';
import useDragEvents from '../../../../hooks/useDragEvents';
import { PlayerTimelineProps, PlayerTimelineRef } from '../../../../types';

function calculatePlayPosition(
  currentTime: number,
  totalDuration?: number
): number {
  if (currentTime < 0) return 0;

  if (!totalDuration || totalDuration === Infinity) return 0;

  const position = (currentTime / totalDuration) * 100;

  return Math.min(position, 100);
}

const LIVE_WIDTH = 100;
// Update interval for the playhead in milliseconds
const TIME_UPDATE_INTERVAL = 250; // 250 milliseconds

const PlayerTimeline = forwardRef<PlayerTimelineRef, PlayerTimelineProps>(
  (
    {
      isVisible,
      bufferedTimeSeconds = 0,
      totalDurationSeconds = -1,
      onSeeking,
      onDragStart,
      onDragEnd,
      currentTimebarStyle,
      progressBarStyle,
      playheadStyle,
      playerVideoRef,
      isLive,
      isWatchingLive,
      setIsWatchingLive,
      goLive,
      calculateIsWatchingLive,
      setHasManuallySeekd,
    },
    playerTimelineRef
  ) => {
    const { currentTime: currentTimeInSeconds, updateTime } =
      usePlayerVideoCurrentTime({
        playerVideoRef,
        updateCurrentTimeInterval: TIME_UPDATE_INTERVAL,
      });

    const [isHovered, setIsHovered] = useState(false);
    const progressBarRef = useRef<View>(null);
    const tooltipRef = useRef<View>(null);
    const [tooltipWidth, setTooltipWidth] = useState(0);

    const {
      dragging,
      dragPercentage,
      dragTime,
      handleMouseDown,
      handleTouchMove,
      tooltipPositionPercentage,
      tooltipTime,
    } = useDragEvents({
      currentTimeInSeconds,
      onDragEnd,
      onDragStart,
      onSeeking,
      progressBarRef,
      totalDurationSeconds,
      setIsWatchingLive,
      calculateIsWatchingLive,
      isLive,
      setHasManuallySeekd,
    });

    const currentTime =
      useMemo(() => {
        if (dragging) {
          return dragTime;
        } else {
          return currentTimeInSeconds;
        }
      }, [dragging, dragTime, currentTimeInSeconds]) || 0;

    const currentPercentage = useMemo(() => {
      if (isLive && isWatchingLive) {
        return LIVE_WIDTH;
      }

      if (!totalDurationSeconds) return 0;

      // For both live and non-live streams, calculate percentage directly
      return (currentTimeInSeconds / totalDurationSeconds) * 100;
    }, [currentTimeInSeconds, totalDurationSeconds, isLive, isWatchingLive]);

    const playPosition = useMemo(() => {
      if (isLive && isWatchingLive) {
        return LIVE_WIDTH;
      } else {
        // For both live and non-live streams, calculate position directly
        return calculatePlayPosition(currentTime, totalDurationSeconds);
      }
    }, [currentTime, totalDurationSeconds, isLive, isWatchingLive]);

    const bufferedTimePercentage = useMemo(() => {
      if (isLive && isWatchingLive) {
        return LIVE_WIDTH;
      } else {
        // For both live and non-live streams, calculate buffered percentage directly
        return totalDurationSeconds
          ? (bufferedTimeSeconds / totalDurationSeconds) * 100
          : 0;
      }
    }, [bufferedTimeSeconds, totalDurationSeconds, isLive, isWatchingLive]);

    const seekToTime = useMemo(() => {
      if (isLive) {
        // For live streams, always show negative time relative to the latest position
        // This matches YouTube's behavior where the tooltip shows time relative to live
        const timeDiff = totalDurationSeconds - dragTime;

        return `-${formatTooltipDuration(timeDiff)}`;
      }

      // For non-live streams, show absolute time
      return formatTooltipDuration(dragTime);
    }, [dragTime, isLive, totalDurationSeconds]);

    useImperativeHandle(playerTimelineRef, () => ({
      fastSeekUpdate() {
        //no-op
      },
      updateTime,
    }));

    /**
     * When the component mounts, go live if the stream is live
     */
    useEffect(() => {
      if (isLive) {
        goLive?.();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Measure tooltip width on mount and when content changes
    useEffect(() => {
      if (tooltipRef.current) {
        tooltipRef.current.measure((_, __, width) => {
          setTooltipWidth(width);
        });
      }
    }, [currentTime, dragTime, tooltipTime]);

    return (
      <KitFadeView show={isVisible} keepElementSize style={styles.container}>
        {totalDurationSeconds > 0 ? (
          <View style={styles.barContainer}>
            <View
              ref={progressBarRef}
              style={[
                styles.progressBar,
                isLive ? { flex: 1 } : null,
                progressBarStyle,
              ]}
            >
              {/* Buffered time bar */}
              <View
                style={[
                  styles.bufferedTimeBar,
                  {
                    width: `${bufferedTimePercentage}%`,
                  },
                ]}
              />
              <View
                style={[
                  styles.currentTimeBar,
                  currentTimebarStyle,
                  {
                    width: `${dragging ? dragPercentage : currentPercentage}%`,
                    ...(Platform.OS === 'web' &&
                      !dragging && {
                        transition: 'width .1s ease-out',
                      }),
                  },
                ]}
              />
            </View>

            <View
              style={[
                styles.playheadContainer,
                {
                  left: `${playPosition}%`,
                  ...(Platform.OS === 'web' &&
                    !dragging && {
                      transition: 'left .1s ease-out',
                    }),
                },
              ]}
            >
              <View style={[styles.playhead, playheadStyle]} />
            </View>

            {/** Logic used to show tooltip for Web */}
            {dragging || isHovered ? (
              <View
                style={[
                  styles.tooltipContainer,
                  {
                    left: `${tooltipPositionPercentage}%`,
                    marginLeft: -tooltipWidth,
                  },
                ]}
              >
                <View ref={tooltipRef}>
                  <KitText style={styles.tooltipTime}>
                    {dragging
                      ? seekToTime
                      : isLive
                      ? `-${formatTooltipDuration(
                          Math.abs(totalDurationSeconds - tooltipTime)
                        )}`
                      : formatTooltipDuration(Math.abs(tooltipTime))}
                  </KitText>
                </View>
              </View>
            ) : null}

            {Platform.OS === 'web' ? (
              <div
                style={styles.pressableContainer}
                onMouseDown={handleMouseDown}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onTouchStart={handleTouchMove}
                onTouchEnd={() => setIsHovered(false)}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            ) : null}
          </View>
        ) : null}
      </KitFadeView>
    );
  }
);

const BarHeight = 4;
const PlayheadSize = 18;
const FontSize = 16;
const PlayheadContainerWidth = 200;
const PressableContainerHeight = 25;

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    zIndex: 2,
  },
  pressableContainer: {
    height: PressableContainerHeight,
    position: 'absolute',
    top: -(PressableContainerHeight - BarHeight) / 2,
    width: '100%',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  barContainer: {
    borderRadius: BorderRadius.full,
    height: BarHeight,
    position: 'relative',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  progressBar: {
    alignSelf: 'center',
    width: '100%',
    height: Spacing.xs,
    backgroundColor: '#ffffff66',
    borderRadius: Spacing.xxs,
    overflow: 'hidden',
  },
  bufferedTimeBar: {
    position: 'absolute',
    height: Spacing.xs,
    backgroundColor: transparentHexColor(Colors.N0, 50),
    borderRadius: Spacing.xxs,
    bottom: 0,
  },
  currentTimeBar: {
    position: 'absolute',
    alignSelf: 'flex-start',
    height: Spacing.xs,
    bottom: 0,
    backgroundColor: Colors.N0,
  },
  /**
   * Takes care of the positioning of playhead so all elements can live inside
   * and offset vertically from it, but align horizontally to its center.
   */
  playheadContainer: {
    position: 'absolute',
    alignItems: 'center',
    marginLeft: -(PlayheadContainerWidth / 2),
    height: BarHeight,
    width: PlayheadContainerWidth,
  },
  playhead: {
    backgroundColor: Colors.N0,
    borderRadius: BorderRadius.full,
    height: PlayheadSize,
    top: -7,
    width: PlayheadSize,
  },
  tooltipContainer: {
    position: 'absolute',
    backgroundColor: transparentHexColor(Colors.N1000, 80),
    borderRadius: BorderRadius.full,
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.s,
    bottom: Spacing.l,
    pointerEvents: 'none',
  },
  tooltipTime: {
    color: Colors.N0,
    fontSize: FontSize,
  },
  currentTimeLive: {
    marginLeft: Spacing.xl,
  },
});

export default PlayerTimeline;
